// Deployment trigger: 2026-04-24
import React, { useState, useEffect } from 'react';
import { ArrowLeft, Trophy, Users, Loader2, CheckCircle2, AlertCircle, PlaySquare, Gift } from 'lucide-react';
import { Campaign, Lead, Prize } from '../types';
import { db } from '../firebase';
import { collection, query, where, getDocs, updateDoc, doc, serverTimestamp } from 'firebase/firestore';
import LiveDraw from './LiveDraw';

interface DrawWinnersDashboardProps {
  campaign: Campaign;
  onBack: () => void;
}

const DrawWinnersDashboard: React.FC<DrawWinnersDashboardProps> = ({ campaign, onBack }) => {
  const [leads, setLeads] = useState<Lead[]>([]);
  const [loading, setLoading] = useState(true);
  const [drawing, setDrawing] = useState(false);
  const [winners, setWinners] = useState<Lead[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [liveDrawPrizes, setLiveDrawPrizes] = useState<Prize[] | null>(null);
  const [activeTab, setActiveTab] = useState<'winners' | 'entries'>('winners');

  useEffect(() => {
    const fetchLeads = async () => {
      try {
        const q = query(
          collection(db, 'leads'), 
          where('campaignId', '==', campaign.id),
          where('projectId', '==', campaign.projectId)
        );
        const snapshot = await getDocs(q);
        const leadsData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Lead));
        setLeads(leadsData);
        
        // Check if there are already winners
        const existingWinners = leadsData.filter(l => l.prizeWon && l.prizeWon !== 'Raffle Entry' && l.prizeWon !== 'N/A');
        if (existingWinners.length > 0) {
          setWinners(existingWinners);
        }
      } catch (err) {
        console.error("Error fetching leads:", err);
        setError("Failed to load entries.");
      } finally {
        setLoading(false);
      }
    };

    fetchLeads();
  }, [campaign.id, campaign.projectId]);

  const handleDraw = async (prize: Prize) => {
    if (leads.length === 0) {
      setError("No entries to draw from.");
      return;
    }

    const existingWinnersForPrize = winners.filter(w => w.prizeWon === prize.name);
    if (existingWinnersForPrize.length > 0) {
      if (!window.confirm(`Winners have already been picked for ${prize.name}. Are you sure you want to draw again? This will add new winners.`)) {
        return;
      }
    }

    setDrawing(true);
    setError(null);

    try {
      // Filter out existing winners so they don't win again
      const eligibleLeads = leads.filter(l => !l.prizeWon || l.prizeWon === 'Raffle Entry' || l.prizeWon === 'N/A');
      
      if (eligibleLeads.length === 0) {
        setError("All entries have already won a prize.");
        setDrawing(false);
        return;
      }

      const countToDraw = prize.winnersCount || 1;
      const actualCount = Math.min(countToDraw, eligibleLeads.length);
      
      // Randomly select winners
      const shuffled = [...eligibleLeads].sort(() => 0.5 - Math.random());
      const selectedWinners = shuffled.slice(0, actualCount);

      // Update in Firestore
      const updatePromises = selectedWinners.map(winner => 
        updateDoc(doc(db, 'leads', winner.id), {
          prizeWon: prize.name,
          wonAt: serverTimestamp()
        })
      );

      await Promise.all(updatePromises);

      // Update campaign status
      await updateDoc(doc(db, 'campaigns', campaign.id), {
        winnersPicked: true,
        updatedAt: serverTimestamp()
      });

      // Update local state
      const newWinners = selectedWinners.map(w => ({ ...w, prizeWon: prize.name }));
      setWinners(prev => [...prev, ...newWinners]);
      
      // Update leads list to reflect new winners
      setLeads(prev => prev.map(l => {
        const isWinner = newWinners.find(w => w.id === l.id);
        return isWinner ? { ...l, prizeWon: prize.name } : l;
      }));

    } catch (err) {
      console.error("Error drawing winners:", err);
      setError("Failed to draw winners. Please try again.");
    } finally {
      setDrawing(false);
    }
  };

  return (
    <div className="bg-white rounded-[32px] p-8 sm:p-12 border border-slate-200 shadow-sm animate-in fade-in slide-in-from-bottom-4">
      <div className="flex items-center justify-between mb-10">
        <div className="flex items-center gap-4">
          <button 
            onClick={onBack}
            className="p-3 bg-slate-50 hover:bg-slate-100 rounded-2xl text-slate-500 transition-colors"
          >
            <ArrowLeft className="w-6 h-6" />
          </button>
          <div>
            <h2 className="text-2xl font-black text-slate-800 uppercase tracking-tight italic">Draw Winners</h2>
            <p className="text-sm font-medium text-slate-500">{campaign.name}</p>
          </div>
        </div>
      </div>

      {error && (
        <div className="mb-8 p-4 bg-red-50 border border-red-100 rounded-2xl flex items-center gap-3 text-red-600">
          <AlertCircle className="w-5 h-5" />
          <p className="text-sm font-bold">{error}</p>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Stats & Draw Action */}
        <div className="lg:col-span-1 space-y-6">
          <div className="bg-slate-50 rounded-[24px] p-6 border border-slate-100">
            <div className="flex items-center gap-3 mb-4 text-slate-500">
              <Users className="w-5 h-5" />
              <h3 className="text-xs font-black uppercase tracking-widest">Total Entries</h3>
            </div>
            <div className="text-4xl font-black text-slate-800">
              {loading ? <Loader2 className="w-8 h-8 animate-spin text-slate-300" /> : leads.length}
            </div>
          </div>

          <div className="bg-indigo-50 rounded-[24px] p-6 border border-indigo-100">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3 text-indigo-600">
                <Trophy className="w-5 h-5" />
                <h3 className="text-xs font-black uppercase tracking-widest">Total Prizes</h3>
              </div>
              <div className="text-2xl font-black text-indigo-700">
                {campaign.prizes.length}
              </div>
            </div>
            <button
              onClick={() => {
                const validPrizes = campaign.prizes.filter(p => {
                  if (p.type === 'empty') return false;
                  const prizeWinners = winners.filter(w => w.prizeWon === p.name);
                  return prizeWinners.length < (p.winnersCount || 1);
                });
                
                if (validPrizes.length === 0) {
                  setError("All valid prizes have already reached their maximum winner count.");
                  return;
                }
                setLiveDrawPrizes(validPrizes);
              }}
              disabled={loading || drawing || leads.length === 0}
              className="w-full py-3 bg-indigo-600 hover:bg-indigo-700 disabled:bg-indigo-300 text-white rounded-xl font-black uppercase tracking-widest transition-all shadow-lg shadow-indigo-600/20 flex items-center justify-center gap-2 text-sm"
            >
              <PlaySquare className="w-4 h-4" />
              Live Draw All Prizes
            </button>
          </div>

          <div className="space-y-4">
            <h3 className="text-sm font-black text-slate-800 uppercase tracking-widest mb-2">Draw by Prize</h3>
            {campaign.prizes.map((prize, idx) => {
              const prizeWinners = winners.filter(w => w.prizeWon === prize.name);
              const maxWinners = prize.winnersCount || 1;
              const isFull = prizeWinners.length >= maxWinners;
              const isDrawn = prizeWinners.length > 0;
              
              return (
                <div key={prize.id} className={`bg-white border rounded-2xl p-4 shadow-sm transition-all ${isFull ? 'border-emerald-200 bg-emerald-50/10' : 'border-slate-200'}`}>
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      {prize.imageUrl ? (
                        <div className="w-10 h-10 rounded-full overflow-hidden border border-slate-200 shrink-0">
                          <img src={prize.imageUrl} alt={prize.name} className="w-full h-full object-cover" referrerPolicy="no-referrer" />
                        </div>
                      ) : (
                        <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center text-amber-600 font-bold text-sm shrink-0">
                          #{idx + 1}
                        </div>
                      )}
                      <div>
                        <p className="font-bold text-slate-800 text-sm">{prize.name}</p>
                        <p className="text-[10px] text-slate-500 font-medium uppercase tracking-wider">{maxWinners} Winner{maxWinners > 1 ? 's' : ''}</p>
                      </div>
                    </div>
                    {isDrawn && (
                      <span className={`px-2 py-1 rounded-lg text-[10px] font-bold uppercase tracking-widest flex items-center gap-1 ${isFull ? 'bg-emerald-500 text-white' : 'bg-emerald-100 text-emerald-700'}`}>
                        <CheckCircle2 className="w-3 h-3" />
                        {isFull ? 'Full' : 'Drawn'}
                      </span>
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      onClick={() => handleDraw(prize)}
                      disabled={loading || drawing || leads.length === 0}
                      className="py-2 bg-slate-100 hover:bg-slate-200 disabled:bg-slate-50 disabled:text-slate-400 text-slate-700 rounded-xl text-xs font-black uppercase tracking-widest transition-colors flex items-center justify-center gap-2"
                    >
                      <Trophy className="w-4 h-4" />
                      Quick Draw
                    </button>
                    <button
                      onClick={() => {
                        if (isFull) {
                          if (!window.confirm(`This prize (${prize.name}) already has the maximum number of winners (${maxWinners}). Are you sure you want to draw again?`)) {
                            return;
                          }
                        }
                        setLiveDrawPrizes([prize]);
                      }}
                      disabled={loading || drawing || leads.length === 0}
                      className="py-2 bg-indigo-600 hover:bg-indigo-700 disabled:bg-slate-200 disabled:text-slate-400 text-white rounded-xl text-xs font-black uppercase tracking-widest transition-colors shadow-sm flex items-center justify-center gap-2"
                    >
                      <PlaySquare className="w-4 h-4" />
                      Live Draw
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Winners & Entries List */}
        <div className="lg:col-span-2">
          <div className="bg-white border border-slate-200 rounded-[24px] overflow-hidden">
            <div className="p-6 border-b border-slate-100 bg-slate-50 flex items-center justify-between">
              <div className="flex items-center gap-4">
                <button
                  onClick={() => setActiveTab('winners')}
                  className={`text-sm font-black uppercase tracking-widest transition-colors ${activeTab === 'winners' ? 'text-slate-800' : 'text-slate-400 hover:text-slate-600'}`}
                >
                  Selected Winners
                </button>
                <button
                  onClick={() => setActiveTab('entries')}
                  className={`text-sm font-black uppercase tracking-widest transition-colors ${activeTab === 'entries' ? 'text-slate-800' : 'text-slate-400 hover:text-slate-600'}`}
                >
                  Eligible Entries
                </button>
              </div>
              <span className="px-3 py-1 bg-emerald-100 text-emerald-700 rounded-full text-[10px] font-bold uppercase tracking-widest">
                {activeTab === 'winners' ? winners.length : leads.filter(l => !l.prizeWon || l.prizeWon === 'Raffle Entry' || l.prizeWon === 'N/A').length} Found
              </span>
            </div>
            
            <div className="p-6">
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-slate-300" />
                </div>
              ) : activeTab === 'winners' ? (
                winners.length === 0 ? (
                  <div className="text-center py-12">
                    <Trophy className="w-12 h-12 text-slate-200 mx-auto mb-4" />
                    <p className="text-slate-500 font-medium">No winners drawn yet.</p>
                    <p className="text-xs text-slate-400 mt-1">Click the draw button to select winners randomly.</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {winners.map((winner, index) => (
                      <div key={winner.id} className="flex items-center justify-between p-4 rounded-2xl border border-amber-100 bg-amber-50/30">
                        <div className="flex items-center gap-4">
                          <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center text-amber-600 font-black">
                            #{index + 1}
                          </div>
                          <div>
                            <p className="font-bold text-slate-800">{winner.name || 'Anonymous'}</p>
                            <div className="flex items-center gap-3 text-xs text-slate-500 mt-1">
                              <span>{winner.email}</span>
                              {winner.phone && (
                                <>
                                  <span className="w-1 h-1 rounded-full bg-slate-300" />
                                  <span>{winner.phone}</span>
                                </>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="inline-flex items-center gap-1.5 px-3 py-1 bg-amber-500 text-white rounded-lg text-[10px] font-black uppercase tracking-widest mb-1">
                            <Gift className="w-3 h-3" />
                            {winner.prizeWon}
                          </div>
                          {winner.ticketNumber && (
                            <p className="text-[10px] font-mono text-slate-400 mt-1">Ticket: {winner.ticketNumber}</p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )
              ) : (
                // Entries Tab
                leads.filter(l => !l.prizeWon || l.prizeWon === 'Raffle Entry' || l.prizeWon === 'N/A').length === 0 ? (
                  <div className="text-center py-12">
                    <Users className="w-12 h-12 text-slate-200 mx-auto mb-4" />
                    <p className="text-slate-500 font-medium">No eligible entries found.</p>
                    <p className="text-xs text-slate-400 mt-1">Share your campaign to get more entries.</p>
                  </div>
                ) : (
                  <div className="space-y-4 max-h-[600px] overflow-y-auto pr-2 scrollbar-hide">
                    {leads.filter(l => !l.prizeWon || l.prizeWon === 'Raffle Entry' || l.prizeWon === 'N/A').map((lead, index) => (
                      <div key={lead.id} className="flex items-center justify-between p-4 rounded-2xl border border-slate-100 bg-slate-50">
                        <div className="flex items-center gap-4">
                          <div className="w-10 h-10 rounded-full bg-slate-200 flex items-center justify-center text-slate-500 font-black text-xs">
                            {index + 1}
                          </div>
                          <div>
                            <p className="font-bold text-slate-800">{lead.name || 'Anonymous'}</p>
                            <div className="flex items-center gap-3 text-xs text-slate-500 mt-1">
                              <span>{lead.email}</span>
                              {lead.phone && (
                                <>
                                  <span className="w-1 h-1 rounded-full bg-slate-300" />
                                  <span>{lead.phone}</span>
                                </>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="inline-flex items-center gap-1.5 px-3 py-1 bg-slate-200 text-slate-600 rounded-lg text-[10px] font-black uppercase tracking-widest mb-1">
                            Eligible
                          </div>
                          {lead.ticketNumber && (
                            <p className="text-[10px] font-mono text-slate-400 mt-1">Ticket: {lead.ticketNumber}</p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )
              )}
            </div>
          </div>
        </div>
      </div>

      {liveDrawPrizes && (
        <LiveDraw 
          leads={leads}
          prizes={liveDrawPrizes}
          onClose={() => setLiveDrawPrizes(null)}
          onDrawComplete={async (selectedWinners, prize) => {
            try {
              // Update in Firestore
              const updatePromises = selectedWinners.map(winner => 
                updateDoc(doc(db, 'leads', winner.id), {
                  prizeWon: prize.name,
                  wonAt: serverTimestamp()
                })
              );
              await Promise.all(updatePromises);

              // Update campaign status
              await updateDoc(doc(db, 'campaigns', campaign.id), {
                winnersPicked: true,
                updatedAt: serverTimestamp()
              });

              // Update local state
              const newWinners = selectedWinners.map(w => ({ ...w, prizeWon: prize.name }));
              setWinners(prev => [...prev, ...newWinners]);
              setLeads(prev => prev.map(l => {
                const isWinner = newWinners.find(w => w.id === l.id);
                return isWinner ? { ...l, prizeWon: prize.name } : l;
              }));
            } catch (err) {
              console.error("Error saving live draw winners:", err);
              setError("Failed to save winners from live draw.");
            }
          }}
        />
      )}
    </div>
  );
};

export default DrawWinnersDashboard;
